import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { setArtifactPanelWidth } from "../midasPageSlice";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  Constants
// ===================================================================

const MIN_PANEL_WIDTH = 360;
const MAX_PANEL_PERCENT = 0.66; // ~2/3 of viewport (Claude Desktop style)

// ===================================================================
//  MidasDragHandle — 4px resize strip between chat and artifact panel
// ===================================================================

const MidasDragHandle: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const vw = window.innerWidth;
      const maxWidth = Math.floor(vw * MAX_PANEL_PERCENT);
      // Panel width = distance from right edge of viewport to cursor
      const newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(maxWidth, vw - e.clientX));
      dispatch(setArtifactPanelWidth(newWidth));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dispatch]);

  const showDots = isHovered || isDragging;

  return (
    <Box
      role="separator"
      aria-label={translate("midas.aria.dragHandle") || "Drag to resize artifact panel"}
      aria-orientation="vertical"
      tabIndex={0}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: "4px",
        cursor: "col-resize",
        backgroundColor: isDragging ? dt.blue : "transparent",
        transition: isDragging ? "none" : "background-color 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        position: "relative",
        "&:hover": {
          backgroundColor: dt.cardBorder,
        },
      }}
    >
      {/* Vertical dots indicator (VS Code style) */}
      {showDots && (
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            pointerEvents: "none",
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: "2px",
                height: "2px",
                borderRadius: "50%",
                backgroundColor: isDragging ? "#FFFFFF" : dt.textMuted,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MidasDragHandle;
